import { type ProspectNoteDto, useSalesGetProspect, useSalesGetProspects } from "@crowdcoursing/api/openapi";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { useTaskDashboard } from "~hooks/useTaskDashboard";
import { AddContactModal } from "~ui/molecules/AddContactModal/AddContactModal";
import { AddNoteModal } from "~ui/molecules/AddNoteModal/AddNoteModal";
import { ContactCard } from "~ui/molecules/ContactCard/ContactCard";
import { NoteCard } from "~ui/molecules/NoteCard/NoteCard";
import { NoteForm } from "~ui/molecules/NoteForm/NoteForm";
import { useContactModal } from "~ui/pages/SalesPortal/useContactModal";
import { useNoteModal } from "~ui/pages/SalesPortal/useNoteModal";
import { SalesTaskPreviewTemplate } from "~ui/templates/SalesTaskPreviewTemplate/SalesTaskPreviewTemplate";
import { TaskDashboardTemplate } from "~ui/templates/TaskDashboardTemplate/TaskDashboardTemplate";
import { type CarouselTaskItem, type ProspectEnrichmentData } from "~ui/templates/TaskDashboardTemplate/taskUtils";

export const TaskDashboardPage = () => {
  const { currentTaskType, setCurrentTaskType, salesTasks, roomTasks, eventTasks } = useTaskDashboard();

  // Track the selected task
  const [selectedTask, setSelectedTask] = useState<CarouselTaskItem | null>(null);

  // Get the selected sales task's prospect details if available
  const selectedSalesTask = selectedTask?.type === "sales" ? selectedTask : null;
  const prospectId = selectedSalesTask?.prospectId;

  // Initialize modals for editing/deleting contacts and notes
  const contactModal = useContactModal(prospectId ?? "");
  const noteModal = useNoteModal(prospectId ?? "");

  // Fetch prospect details when a sales task is selected
  const { data: prospect, isLoading: isLoadingProspect } = useSalesGetProspect(
    { pathParams: { id: prospectId ?? "" } },
    { enabled: !!prospectId },
  );

  // Fetch all prospects to get region and timeZone for chips
  const { data: allProspects, isLoading: isLoadingProspects } = useSalesGetProspects({});

  // Build the prospects map for enrichment (region, timeZone) and extract unique regions for filters
  const { prospectsMap, regions } = useMemo(() => {
    if (!allProspects) {
      return { prospectsMap: undefined, regions: [] };
    }
    const map = new Map<string, ProspectEnrichmentData>();
    const regionsSet = new Set<string>();

    for (const prospect of allProspects) {
      if (prospect.id) {
        map.set(prospect.id, {
          id: prospect.id,
          region: prospect.region,
          timeZone: prospect.timeZone,
        });

        // Collect unique regions
        if (prospect.region) {
          regionsSet.add(prospect.region);
        }
      }
    }

    return {
      prospectsMap: map,
      regions: Array.from(regionsSet).sort(),
    };
  }, [allProspects]);

  // Show loading state if any of the task data is loading
  const isLoading = salesTasks.isLoading || roomTasks.isLoading || eventTasks.isLoading || isLoadingProspects;

  const handleTaskSelect = (task: CarouselTaskItem) => {
    setSelectedTask(task);
  };

  if (isLoading || isLoadingProspect) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: "100vh", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Determine which task content to show based on the selected task type
  const renderTaskContent = () => {
    if (!selectedTask) {
      return null;
    }

    switch (selectedTask.type) {
      case "sales":
        return <SalesTaskPreviewTemplate selectedTask={selectedTask} />;
      case "room":
        // Room task template will be implemented later
        return null;
      case "event":
        // Event task template will be implemented later
        return null;
      default:
        return null;
    }
  };

  // Find the primary contact or fallback to first contact
  const contact = prospect?.contacts?.find((contact) => contact.isPrimary) ?? prospect?.contacts?.[0];

  return (
    <TaskDashboardTemplate
      currentTaskType={currentTaskType}
      onTaskTypeChange={setCurrentTaskType}
      salesTasks={salesTasks}
      roomTasks={roomTasks}
      eventTasks={eventTasks}
      onTaskSelect={handleTaskSelect}
      selectedTask={selectedTask}
      prospectsMap={prospectsMap}
      regions={regions}
      taskContentArea={
        <>
          {/* Task Details Section */}
          {selectedTask && (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xl: "1fr 1fr",
                  xs: "1fr",
                },
                mb: 3,
              }}
            >
              {/* Contact Info Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                {contact && prospectId ? (
                  <ContactCard
                    contact={contact}
                    onEdit={contactModal.openEditModal}
                    onDelete={contactModal.openDeleteModal}
                  />
                ) : (
                  <Typography color="text.secondary">No contact information available</Typography>
                )}
              </Box>

              {/* Notes Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Box
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {prospectId && prospect?.notes?.length ? (
                    prospect.notes.map((note: ProspectNoteDto) => (
                      <Box key={note.id} sx={{ mb: 2 }}>
                        <NoteCard note={note} noteModal={noteModal} />
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                      <Typography color="text.secondary">No notes available</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Task Preview Content */}
          {renderTaskContent()}

          {/* Contact Modals */}
          {contactModal.isAddOpen && (
            <AddContactModal
              open={contactModal.isAddOpen}
              onClose={contactModal.closeAddModal}
              onSubmit={contactModal.handleAddContact}
            />
          )}
          {contactModal.editContact && (
            <AddContactModal
              open={true}
              onClose={contactModal.closeEditModal}
              onSubmit={contactModal.handleUpdateContact}
              initialValues={{
                email: contactModal.editContact?.email ?? undefined,
                firstName: contactModal.editContact?.firstName ?? "",
                isPrimary: contactModal.editContact?.isPrimary ?? false,
                lastName: contactModal.editContact?.lastName ?? "",
                phone: contactModal.editContact?.phone ?? undefined,
                roleTitle: contactModal.editContact?.roleTitle ?? undefined,
              }}
              isEdit
            />
          )}

          {/* Delete Contact Confirmation Dialog */}
          <Dialog open={contactModal.deleteConfirmOpen} onClose={contactModal.closeDeleteModal} maxWidth="xs" fullWidth>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this contact?{" "}
                {contactModal.contactToDelete && (
                  <>
                    <br />
                    <br />
                    Contact:{" "}
                    <strong>
                      {[contactModal.contactToDelete.firstName, contactModal.contactToDelete.lastName]
                        .filter(Boolean)
                        .join(" ")}
                    </strong>
                    {contactModal.contactToDelete.isPrimary && " (Primary Contact)"}
                  </>
                )}
                <br />
                <br />
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={contactModal.closeDeleteModal}>Cancel</Button>
              <Button onClick={contactModal.handleDeleteContact} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Note Modals */}
          {noteModal.isAddOpen && (
            <AddNoteModal
              open={noteModal.isAddOpen}
              onClose={noteModal.closeAddModal}
              onSubmit={noteModal.handleAddNote}
            />
          )}
          {noteModal.editNote && (
            <Dialog open={true} onClose={noteModal.closeEditModal} maxWidth="sm" fullWidth>
              <DialogTitle>Edit Note</DialogTitle>
              <DialogContent>
                <Box pt={1}>
                  <NoteForm
                    onSubmit={noteModal.handleUpdateNote}
                    initialValues={{
                      body: noteModal.editNote.body ?? "",
                      pinned: noteModal.editNote.pinned ?? false,
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={noteModal.closeEditModal}>Cancel</Button>
                <Button type="submit" form="note-form" variant="contained">
                  Save Changes
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {/* Delete Note Confirmation Dialog */}
          <Dialog open={noteModal.deleteConfirmOpen} onClose={noteModal.closeDeleteModal} maxWidth="xs" fullWidth>
            <DialogTitle>Delete Note</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this note?
                {noteModal.noteToDelete && (
                  <>
                    <br />
                    <br />
                    Note preview:
                    <br />
                    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, mt: 1, p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {noteModal.noteToDelete.body}
                      </Typography>
                    </Box>
                  </>
                )}
                <br />
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={noteModal.closeDeleteModal}>Cancel</Button>
              <Button onClick={noteModal.handleDeleteNote} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
    />
  );
};
